import React, { FC, useEffect, useRef, useState } from 'react'
import { fetchData, getLocalizedName, getTranslatedValue } from '../../../../reusable'
import { getSelectedLang } from '../../../../_metronic/i18n/Metronici18n'
import { Tr } from '../../../../_metronic/i18n/Tr'

export interface PromotionSelection {
  parentId: string
  childIds: string[]
}

interface CategoryConfig {
  parentLabel: string
  childLabel: string
  parentApi: string
  parentParam: (vendorId: string) => object
  parentIdKey: string
  childApi: string
  childParam: (parentId: string) => object
  childListKey: string
  childIdKey: string
  parentOnly?: boolean
}

export const CATEGORY_CONFIG: Record<string, CategoryConfig> = {
  '3': {
    parentLabel: 'Restaurant',
    childLabel: 'Restaurant',
    parentApi: 'public/restaurantList',
    parentParam: (vendorId) => ({ vendorId }),
    parentIdKey: 'restaurantId',
    childApi: '',
    childParam: () => ({}),
    childListKey: '',
    childIdKey: 'restaurantId',
    parentOnly: true,
  },
  '1': {
    parentLabel: 'Hotel',
    childLabel: 'Rooms',
    parentApi: 'public/hotelList',
    parentParam: (vendorId) => ({ vendorId }),
    parentIdKey: 'hotelId',
    childApi: 'public/hotelDetailsV2',
    childParam: (parentId) => ({ hotelId: parentId }),
    childListKey: 'roomList',
    childIdKey: 'roomId',
  },
  '4': {
    parentLabel: 'Space Rental',
    childLabel: 'Rentable Spaces',
    parentApi: 'public/spacerentalList',
    parentParam: (vendorId) => ({ vendorId }),
    parentIdKey: 'spacerentalId',
    childApi: 'public/spaceDetails',
    childParam: (parentId) => ({ spacerentalId: parentId }),
    childListKey: 'rentablespace',
    childIdKey: 'rentablespaceId',
  },
  '5': {
    parentLabel: 'Tour Package',
    childLabel: 'Tours',
    parentApi: 'public/tourpackageList',
    parentParam: (vendorId) => ({ vendorId }),
    parentIdKey: 'tourpackageId',
    childApi: 'public/tourpackageDetails',
    childParam: (parentId) => ({ tourpackageId: parentId }),
    childListKey: 'toursDetail',
    childIdKey: 'toursId',
  },
  '7': {
    parentLabel: 'Car Rental',
    childLabel: 'Vehicles',
    parentApi: 'public/carRentalList',
    parentParam: (vendorId) => ({ vendorId: String(vendorId) }),
    parentIdKey: 'carrentalId',
    childApi: 'public/carRentalDetail',
    childParam: (parentId) => ({ carrentalId: String(parentId) }),
    childListKey: 'vehcileDetail',
    childIdKey: 'vehicleId',
  },
}

// name in the selected language, falling back to the listing's primary language
const getEnName = (item: any): string =>
  getLocalizedName(item, getSelectedLang()) || `Item #${Object.values(item || {})[0]}`

interface Props {
  value: PromotionSelection
  onChange: (selection: PromotionSelection) => void
  error?: string
  defaultChildIds?: string[]
}

const AddAdvertisementPromotionSelect: FC<Props> = ({ value, onChange, error, defaultChildIds }) => {
  const t = Tr()

  const categoryId = sessionStorage.getItem('categoryId') || ''
  const vendorData = sessionStorage.getItem("vendor")
  const vendorId = vendorData ? JSON.parse(vendorData).vendorId : ''

  const cfg = CATEGORY_CONFIG[categoryId]

  const [parentList, setParentList] = useState<any[]>([])
  const [childList, setChildList] = useState<any[]>([])
  const [parentLoading, setParentLoading] = useState(false)
  const [childLoading, setChildLoading] = useState(false)
  const lookupDoneRef = useRef(false)

  useEffect(() => {
    if (!cfg || !vendorId) return
    setParentLoading(true)
    fetchData(cfg.parentApi, cfg.parentParam(vendorId))
      .then((res: any) => {
        if (String(res?.status) === '200') {
          setParentList(res.data || [])
        }
      })
      .finally(() => setParentLoading(false))
  }, [])

  // Restore parent + child selection when editing (defaultChildIds provided)
  useEffect(() => {
    if (lookupDoneRef.current) return
    if (!defaultChildIds?.length) return
    if (value.parentId) return
    if (!parentList.length) return
    if (!cfg || !cfg.parentOnly) return

    lookupDoneRef.current = true

    const matchingParent = parentList.find((parent) =>
      defaultChildIds.includes(String(parent[cfg.parentIdKey]))
    )
    if (matchingParent) {
      const parentId = String(matchingParent[cfg.parentIdKey])
      onChange({ parentId, childIds: [parentId] })
      return
    }

    const findParent = async () => {
      for (const parent of parentList) {
        const parentId = String(parent[cfg.parentIdKey])
        try {
          const res: any = await fetchData(cfg.childApi, cfg.childParam(parentId))
          if (String(res?.status) === '200') {
            const children: any[] = res.data?.[0]?.[cfg.childListKey] || []
            const childrenIds = children.map((c: any) => String(c[cfg.childIdKey]))
            const matching = defaultChildIds.filter(id => childrenIds.includes(id))
            if (matching.length > 0) {
              onChange({ parentId, childIds: matching })
              return
            }
          }
        } catch {
          // continue to next parent
        }
      }
    }

    if (!cfg.parentOnly) findParent()
  }, [parentList, defaultChildIds])

  useEffect(() => {
    if (!cfg || cfg.parentOnly || !value.parentId) {
      setChildList([])
      return
    }
    setChildLoading(true)
    fetchData(cfg.childApi, cfg.childParam(value.parentId))
      .then((res: any) => {
        if (String(res?.status) === '200') {
          const children = res.data?.[0]?.[cfg.childListKey] || []
          setChildList(children)
        }
      })
      .finally(() => setChildLoading(false))
  }, [value.parentId])

  // Unknown categories do not have a selectable promotion target.
  if (!cfg) return null

  const handleParentChange = (parentId: string) => {
    onChange({ parentId, childIds: cfg.parentOnly && parentId ? [parentId] : [] })
  }

  const handleChildToggle = (childId: string) => {
    const newChildIds = value.childIds.includes(childId)
      ? value.childIds.filter((id) => id !== childId)
      : [...value.childIds, childId]
    onChange({ ...value, childIds: newChildIds })
  }

  const handleSelectAll = () => {
    if (value.childIds.length === childList.length) {
      onChange({ ...value, childIds: [] })
    } else {
      onChange({ ...value, childIds: childList.map((c: any) => String(c[cfg.childIdKey])) })
    }
  }

  const allSelected = childList.length > 0 && value.childIds.length === childList.length

  return (
    <div className='col-md-12 mb-5'>
      <label className='form-label fw-bold'>
        {t('Promotion Items')} <span className='text-muted'>({t('Optional')})</span>
      </label>
      <div className='card border p-4'>
        {/* Parent selector */}
        <div className='mb-4'>
          <label className='form-label fw-semibold'>
             {t(cfg.parentLabel)} {t('Select')}
          </label>
          {parentLoading ? (
            <div className='text-muted py-2'>{t('Loading')}...</div>
          ) : (
            <select
              className='form-select'
              value={value.parentId}
              onChange={(e) => handleParentChange(e.target.value)}
            >
              <option value=''>-- {t(cfg.parentLabel)} {t('Select')}  --</option>
              {parentList.map((item: any) => (
                <option key={item[cfg.parentIdKey]} value={String(item[cfg.parentIdKey])}>
                  {getEnName(item)}
                </option>
              ))}
            </select>
          )}
          {parentList.length === 0 && !parentLoading && (
            <small className='text-muted d-block mt-1'>
              {t('No')} {t(cfg.parentLabel)} {t('found')}
            </small>
          )}
        </div>

        {/* Child multi-select */}
        {value.parentId && !cfg.parentOnly && (
          <div>
            <div className='d-flex justify-content-between align-items-center mb-3'>
              <label className='form-label fw-semibold mb-0'>
                 {t(cfg.childLabel)} {t('Select')}
              </label>
              {childList.length > 0 && !childLoading && (
                <button
                  type='button'
                  className='btn btn-sm btn-light-primary'
                  onClick={handleSelectAll}
                >
                  {allSelected ? t('Deselect All') : t('Select All')}
                </button>
              )}
            </div>

            {childLoading ? (
              <div className='text-muted py-2'>{t('Loading')}...</div>
            ) : childList.length === 0 ? (
              <div className='text-muted'>
                {t('No')} {t(cfg.childLabel)} {t('found')}
              </div>
            ) : (
              <div className='row g-3'>
                {childList.map((item: any) => {
                  const childId = String(item[cfg.childIdKey])
                  const isSelected = value.childIds.includes(childId)
                  return (
                    <div className='col-md-4 col-sm-6' key={childId}>
                      <div
                        className={`border rounded p-3 h-100 ${
                          isSelected ? 'border-primary bg-light-primary' : 'border-dashed'
                        }`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleChildToggle(childId)}
                      >
                        <div className='d-flex align-items-center gap-2'>
                          <input
                            type='checkbox'
                            className='form-check-input mt-0 flex-shrink-0'
                            checked={isSelected}
                            onChange={() => handleChildToggle(childId)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span className={`fw-semibold ${isSelected ? 'text-primary' : ''}`}>
                            {getEnName(item)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {value.childIds.length > 0 && (
              <div className='mt-3'>
                <span className='badge badge-light-primary fs-7'>
                  {value.childIds.length} {t(cfg.childLabel.toLowerCase())} {t('selected')}
                </span>
              </div>
            )}
          </div>
        )}

        {error && <div className='text-danger mt-2'>{error}</div>}
      </div>
    </div>
  )
}

export default AddAdvertisementPromotionSelect
